import React from 'react';
import { CheckCircle2, Clock, ArrowRight, Mail, Ticket } from 'lucide-react';

/**
 * Presentational post-submit confirmation card.
 *
 * Renders only when the parent has a `purchaseComplete` object. Lives ABOVE
 * the existing ClaimAccountCard / ClaimSuccessCard — does not replace either.
 *
 * Props:
 *   event            — event object (uses title + release_method)
 *   selectedTier     — { tier_name, ... }
 *   entryQuantity    — integer
 *   selectedAddOns   — { [addonId]: qty }
 *   activeAddOns     — array of add-on tier objects (for name lookup)
 *   purchaseEmail    — email the pass was saved to
 *   ticketId         — string
 *   onViewPass       — () => void
 *   onGoToMyPasses   — () => void
 */
export default function OrderConfirmationCard({
  event,
  selectedTier,
  entryQuantity,
  selectedAddOns = {},
  activeAddOns = [],
  purchaseEmail,
  ticketId,
  onViewPass,
  onGoToMyPasses,
}) {
  const isManual = event?.release_method === 'manual';
  const addonLines = Object.entries(selectedAddOns || {})
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const ao = activeAddOns.find(a => a.id === id);
      return ao ? { name: ao.tier_name, qty } : null;
    })
    .filter(Boolean);

  const maskedEmail = (() => {
    if (!purchaseEmail || !purchaseEmail.includes('@')) return purchaseEmail || '';
    const [name, domain] = purchaseEmail.split('@');
    if (name.length <= 2) return `${name[0] || ''}***@${domain}`;
    return `${name.slice(0, 2)}***@${domain}`;
  })();

  return (
    <div
      className="mb-5 bg-gradient-to-br from-[#0F0D08] to-[#050505] border border-[#D4AF37]/30 rounded-sm overflow-hidden"
      data-testid="order-confirmation-card"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#D4AF37]/20 bg-[#D4AF37]/5 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isManual ? 'bg-[#FFB800]/10' : 'bg-emerald-500/10'}`}>
          {isManual
            ? <Clock className="w-5 h-5 text-[#FFB800]" />
            : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
        </div>
        <div className="min-w-0">
          <div className="text-xs tracking-[0.25em] text-[#D4AF37] uppercase font-semibold">
            Order Confirmation
          </div>
          <div className="text-xl font-black text-[#F3EEE7] leading-tight" data-testid="order-confirmation-heading">
            {isManual ? 'Access Pending' : 'Access Granted'}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-4">
        <p className="text-sm text-[#B9B1A6] leading-relaxed" data-testid="order-confirmation-message">
          {isManual
            ? "Your request has been submitted and is pending approval. You'll be notified once it's reviewed."
            : 'Your pass is ready. Check your email or view it now.'}
        </p>

        {/* Details */}
        <div className="border border-[#1A1A1A] rounded-sm bg-[#050505] divide-y divide-[#1A1A1A]" data-testid="order-confirmation-details">
          {event?.title && (
            <Row label="Event" value={event.title} />
          )}
          {selectedTier?.tier_name && (
            <Row
              label="Pass"
              value={`${selectedTier.tier_name}${entryQuantity > 1 ? ` × ${entryQuantity}` : ''}`}
            />
          )}
          {addonLines.length > 0 && (
            <Row
              label="Add-ons"
              value={
                <div className="space-y-0.5">
                  {addonLines.map((a, i) => (
                    <div key={i}>{a.name}{a.qty > 1 ? ` × ${a.qty}` : ''}</div>
                  ))}
                </div>
              }
            />
          )}
          {purchaseEmail && (
            <Row
              label="Email"
              value={
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-[#777]" />
                  <span className="font-mono">{maskedEmail}</span>
                </span>
              }
            />
          )}
          {ticketId && (
            <Row
              label="Pass ID"
              value={<span className="font-mono text-xs">{ticketId}</span>}
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <button
            type="button"
            onClick={onViewPass}
            className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] text-sm font-bold transition-opacity"
            data-testid="order-confirmation-view-pass"
          >
            <Ticket className="w-4 h-4" />
            View My Pass
          </button>
          <button
            type="button"
            onClick={onGoToMyPasses}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm border border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:border-[#D4AF37]/40 text-sm font-semibold transition-colors"
            data-testid="order-confirmation-my-passes"
          >
            Go to My Passes
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5 min-w-0">
      <div className="text-[10px] tracking-[0.15em] text-[#777] uppercase font-semibold w-20 flex-shrink-0 pt-0.5">
        {label}
      </div>
      <div className="flex-1 min-w-0 text-sm text-[#EAE7E1] break-words">
        {value}
      </div>
    </div>
  );
}
